import { SelectionModel } from '@angular/cdk/collections';
import { ViewportScroller } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacyPaginator as MatPaginator, LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatSort } from '@angular/material/sort';
import { ConfigurationService } from 'projects/zbs-app/src/app/components/configuration-select/configuration.service';
import { Configuration } from 'projects/zbs-app/src/app/services/Ebootis.Queries/Configuration';
import { CustomerSearch } from 'projects/zbs-app/src/app/services/Ebootis.Queries/CustomerSearch';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CustomerService } from '../../customer.service';
import { CustomerTableI18n } from './CustomerTableI18n';

@Component({
	selector: 'app-customer-table',
	templateUrl: './customer-table.component.html',
	styleUrls: ['./customer-table.component.scss'],
	providers: [CustomerTableI18n]
})
export class CustomerTableComponent implements OnDestroy {

	@Input() set setTable(p_value: CustomerSearch.Customer[]) {
		this.m_queryData = p_value;
		this.initAndReinitTable();
	}

	@Input() set selectionMode(p_value: string) {
		this.m_selectionMode = p_value;
		if (this.m_selectionMode == 'item' || this.m_selectionMode == 'list')
			this.m_displayedColumns.unshift('select');
	}

	get selectionMode(): string {
		return this.m_selectionMode;
	}

	@Output() public customersSelected: EventEmitter<CustomerSearch.Customer | CustomerSearch.Customer[]> = new EventEmitter();
	@Output() public customerEdit: EventEmitter<string> = new EventEmitter();

	@ViewChild(MatSort, { static: true }) set matSort(p_sort: MatSort) {
		this.m_dataSource.sort = p_sort;
	}

	@ViewChild(MatPaginator) set matPaginator(p_paginator: MatPaginator) {
		this.m_dataSource.paginator = p_paginator;
	}

	private m_queryData: CustomerSearch.Customer[];
	private m_subscriptionsSubject = new Subject();
	private m_selectionMode: string;
	public m_displayMessage: string;
	public m_displayError: HttpErrorResponse;
	public m_messageHandlerClass: string = 'info';
	public m_configuration: Configuration.Result;
	public m_dataSource: MatTableDataSource<CustomerSearch.Customer>;
	public m_displayedColumns: string[] = ['id', 'companyName1', 'lastName', 'street', 'zip', 'city', 'hierarchy', 'tool'];
	public m_selection = new SelectionModel<CustomerSearch.Customer>(true, []);

	constructor(
		private m_customerService: CustomerService,
		private m_configurationService: ConfigurationService,
		private m_changeDetectorRef: ChangeDetectorRef,
		private m_translation: CustomerTableI18n,
		public m_dialog: MatDialog,
		private viewPortScroller: ViewportScroller
	) {
		this.m_dataSource = new MatTableDataSource<CustomerSearch.Customer>();
		this.m_configurationService.getConfiguration().pipe(
			takeUntil(this.m_subscriptionsSubject)
		).subscribe({
			next: (data) => {
				this.m_configuration = data;
			}
		});
	}

	/**
	 * Die Subscriptions werden geschlossen
	 */
	public ngOnDestroy(): void {
		this.m_subscriptionsSubject.next(null);
		this.m_subscriptionsSubject.complete();
	}

	/**
	 * Prüft ob alle Customer ausgewählt wurden.
	 */
	public isAllSelected() {
		const numSelected = this.m_selection.selected.length;
		const numRows = this.m_dataSource.data.length;
		return numSelected === numRows;
	}

	public page(p_pageEvent: PageEvent) {
		if (p_pageEvent.pageIndex > p_pageEvent.previousPageIndex) {
			document.querySelector('#top').scrollIntoView();
		}
	}

	/**
	 * Prüft den Status der ausgeählten CheckBoxen
	 */
	public masterToggle() {
		this.isAllSelected() ?
			this.m_selection.clear() :
			this.m_dataSource.data.forEach((row) => this.m_selection.select(row));
	}

	/**
	 * Die ausgewählten Customer werden an das aufrufende Component übergeben.
	 * @param p_element Ausgewählte Customer
	 */
	public selectCustomer_Click(p_element): void {
		this.customersSelected.emit(p_element);
	}

	/**
	 * Die OID eines ausgewählten Customers wird an das aufrufende Component übergeben.
	 * @param p_element Ausgewählter Customer
	 */
	public openEdit_Click(p_element): void {
		this.customerEdit.emit(p_element.oid);
	}

	/**
	 * Ein Customer wird aus der Datenbak gelöscht.
	 * @param p_element Ausgewählter Customer
	 */
	public removeCustomer(p_element): void {
		this.m_customerService.removeCustomer(p_element, this.m_configurationService.SelectedConfigOid).pipe(
			takeUntil(this.m_subscriptionsSubject)
		).subscribe({
			next: (data) => {
				this.m_queryData.splice(p_element.oid, 1);
				this.initAndReinitTable();
				this.m_messageHandlerClass = 'success';
				this.m_displayMessage = this.m_translation.customerTableCustomerRemove;
			},
			error: (error) => {
				this.m_displayError = error;
				this.m_messageHandlerClass = 'danger';
			}
		});
	}

	/**
	 * Die Tabelle wird initialisiert oder reinitialisiert.
	 */
	public initAndReinitTable(): void {
		this.m_dataSource.data = this.m_queryData;
		if (this.m_dataSource.paginator) {
			this.m_dataSource.paginator.firstPage();
		}
		this.m_changeDetectorRef.detectChanges();
	}

	public sortAttributeChanged() {
		if (this.m_dataSource.paginator) {
			this.m_dataSource.paginator.firstPage();
		}
	}

	/**
	 * Modaler Dialog zum löschen eines ausgewählten Customers wird aufgerufen.
	 * @param p_element Ausgewählter Customer
	 */
	public openDialog_Click(p_element): void {
		const dialogRef = this.m_dialog.open(CustomerDeleteDialog, {});
		dialogRef.afterClosed().pipe(
			takeUntil(this.m_subscriptionsSubject)
		).subscribe({
			next: (result) => {
				if (result == true) {
					this.removeCustomer(p_element);
				}
			}
		});
	}

	/**
	 * Die Nachrichten des MessageHandlers werden zurückgesetzt.
	 */
	public clearDisplayMessages(): void {
		this.m_displayError = null;
		this.m_displayMessage = null;
	}

	/**
	 * Die ausgewählten Customer werden an das aufrufende Component übergeben.
	 */
	public selectCustomers_Click(): void {
		this.customersSelected.emit(this.m_selection.selected);
	}
}

@Component({
	selector: 'customer-delete-dialog',
	templateUrl: 'customer-delete-dialog.html',
})
export class CustomerDeleteDialog {

	constructor(
		public m_dialogRef: MatDialogRef<CustomerDeleteDialog>
	) { }

	/**
	 * Nein Button. Customer wird nicht gelöscht.
	 */
	public onNo_Click(): void {
		this.m_dialogRef.close(false);
	}

	/**
	 * Ja Button. Customer wird gelöscht.
	 */
	public onYes_Click(): void {
		this.m_dialogRef.close(true);
	}
}
